/**
 * The one list of tags the renderers can draw. Both render gates consult it — the native walk
 * below, and the web sanitiser in `createRuntimeIcon.web.tsx` — so neither can drift from the
 * other, and `iconRenderTags.parity.spec.ts` holds the config-manager copy to it.
 *
 * `use` is deliberately absent. DOMPurify's svg profile drops it outright, so it never rendered on
 * web; keeping it here while native honoured it made the two platforms disagree on markup the
 * config schema accepted. Supporting it would mean allowing `href`, which is forbidden at both
 * gates, so the honest list is one without it.
 */
export const ICON_TAGS = [
    'svg',
    'g',
    'path',
    'rect',
    'circle',
    'ellipse',
    'line',
    'polygon',
    'polyline',
    'defs',
    'clipPath',
    'mask',
    'title',
    'desc',
] as const

const ALLOWED_TAGS = new Set<string>(ICON_TAGS)

interface XmlNode {
    tag?: string
    props?: Record<string, unknown>
    children?: unknown[]
}

/**
 * Structural gate for the native renderer, where DOMPurify has no DOM to work with.
 *
 * Expects react-native-svg's XmlAST — `{ tag, props, children }` with raw children — which is what
 * `parse`'s middleware receives. Walking `parse`'s return value instead would see React elements,
 * whose tag lives on `type`, so everything below the root would pass silently.
 */
export const hasOnlyAllowedTags = (node: unknown): boolean => {
    if (node === null || node === undefined || typeof node === 'string') {
        return true
    }
    const candidate = node as XmlNode
    if (candidate.tag !== undefined && !ALLOWED_TAGS.has(candidate.tag)) {
        return false
    }
    return (candidate.children ?? []).every(hasOnlyAllowedTags)
}
